/**
 * heldContractsTitle — hover text for the solid PosRing (contracts you hold).
 *
 * Single-sourced deliberately (#161). This string is rendered by THREE
 * surfaces — GameTable's PosCell, GameDetail's PosChips and SpreadsTotalsTable
 * — which until now each carried their own copy of the same conditional. A
 * fourth consumer (MultiOutcomeTable) inherits GameTable's rows and needs no
 * wiring of its own.
 *
 * The average is the canonical BEFORE-FEES entry basis carried on the
 * position slice; formatting matches the Positions page's own
 * formatCents(avgEntryPriceCents) at 0.01¢ grain, so one position can never
 * read as two different averages depending on which surface you hover.
 */

import { roundToCentiCents } from '@/lib/pricing/subCent';
import type { UserPositionSlice } from '@/hooks/useUserKalshiPOS';

/** One side's clause. `withNoun` spells out "contracts" only when this is the
 *  sole side — a mixed holding is already unambiguous and stays short. */
function sideClause(
  count: number,
  avgCents: number | null,
  label: 'YES' | 'NO',
  withNoun: boolean,
): string {
  const noun = withNoun ? ` contract${count === 1 ? '' : 's'}` : '';
  const head = `${count} ${label}${noun}`;
  // Parenthesised rather than another `·` clause: the separator already
  // divides the two sides, so a bare "· avg unavailable" would read as a
  // third holding.
  return avgCents == null
    ? `${head} (avg unavailable)`
    : `${head} @ ${roundToCentiCents(avgCents)}¢ avg`;
}

export function heldContractsTitle(pos: UserPositionSlice): string {
  const mixed = pos.yesCount > 0 && pos.noCount > 0;
  const parts: string[] = [];
  if (pos.yesCount > 0) parts.push(sideClause(pos.yesCount, pos.yesAvgCents, 'YES', !mixed));
  if (pos.noCount > 0) parts.push(sideClause(pos.noCount, pos.noAvgCents, 'NO', !mixed));
  return parts.join(' · ');
}

/**
 * Same hover, for a ring whose counts span SEVERAL markets — the Spreads/
 * Totals strip, which deliberately aggregates every line so holdings stay
 * visible whichever chip is selected.
 *
 * One average across those lines would blend unrelated strikes (your over-7.5
 * at 80¢ with your over-9.5 at 30¢), so each held line is listed with its own
 * average instead. That is the choice this strip already makes for open orders
 * — it enumerates the lines rather than collapsing them — and it matches the
 * order ladder, which pairs a blended price with a per-market breakdown the
 * moment the aggregate spans more than one market.
 */
export function heldContractsByLineTitle(
  entries: readonly { lineLabel: string | null; pos: UserPositionSlice }[],
): string {
  return entries
    .map((e) =>
      e.lineLabel ? `${e.lineLabel} — ${heldContractsTitle(e.pos)}` : heldContractsTitle(e.pos),
    )
    .join('\n');
}
